import { Box, Button, FormControl, InputLabel, MenuItem, Modal, Select, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import {
  FinancialPaymentMethod,
  FinancialPaymentMethodLabel
} from "../enum/financialEnums";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";
import { DueSaleInterface } from "./SaleDueContext";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  sale: DueSaleInterface | null;
}

const fmtCurrency = (value: string | number) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Confirma o recebimento de uma venda a prazo — mesma ideia de
 * FinancialEntrySettleModal, mas chamando PATCH /sales/:id/confirm-payment
 * (que já liquida o lançamento financeiro vinculado por baixo dos panos).
 */
const SaleConfirmPaymentModal: React.FC<Props> = ({ open, onClose, onConfirmed, sale }) => {
  const [paymentMethod, setPaymentMethod] = useState<FinancialPaymentMethod | "">("");
  const [submitting, setSubmitting] = useState(false);

  const containerId = "toaster-sale-confirm-payment-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    if (open) setPaymentMethod("");
  }, [open]);

  const handleSubmit = async () => {
    if (!sale) return;
    if (!paymentMethod) {
      autoError("Selecione a forma de pagamento usada.");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/sales/${sale.sale_id}/confirm-payment`, {
        payment_method: paymentMethod
      });
      autoSuccess("Pagamento confirmado com sucesso!");
      onConfirmed();
      onClose();
    } catch (error) {
      handleHttpError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 380
          }}
        >
          <Typography variant="h6" fontWeight={800} mb={0.5}>
            Confirmar recebimento
          </Typography>
          {sale && (
            <Typography sx={{ color: "var(--text-muted)" }}>
              Venda #{sale.sale_number} — {fmtCurrency(sale.amount)}
            </Typography>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Forma de pagamento</InputLabel>
            <Select
              label="Forma de pagamento"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as FinancialPaymentMethod)}
            >
              {Object.values(FinancialPaymentMethod).map((m) => (
                <MenuItem key={m} value={m}>
                  {FinancialPaymentMethodLabel[m]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              Confirmar
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SaleConfirmPaymentModal;
